/**
 * The layer context menu, in one place.
 *
 * Right-clicking a layer means the same thing wherever it is done — on the
 * canvas, or on its row in the Layers list — so the rows are built once here
 * and both callers render them through the same menu component. Each row calls
 * the store action the keyboard shortcut already calls, so there is still one
 * implementation of "bring to front", not three.
 *
 * Callers differ only at the edges: the canvas knows a point to paste at and a
 * list of layers under the pointer, the panel knows how to start a rename.
 */
import {
    IconCopy, IconClipboard, IconCopyPlus, IconCut, IconTrash, IconCursorText,
    IconArrowBarToUp, IconArrowBarToDown, IconArrowUp, IconArrowDown,
    IconBoxMultiple, IconBoxMultipleFilled, IconMask, IconMaskOff, IconVectorBezier2,
    IconCrop, IconFlipHorizontal, IconFlipVertical,
    IconEye, IconEyeOff, IconLock, IconLockOpen, IconFrame, IconCopyPlusFilled,
    IconSelectAll,
} from '@tabler/icons-react';
import { ELEMENT_TYPES, getFrames } from '../schema/documentSchema';

/**
 * "Paste here" from a MENU cannot read the system clipboard the way ⌘V can — a
 * paste event carries its data, a menu click does not. So the in-app clipboard
 * is tried first and the system one asked for only when there is nothing there,
 * which is also what keeps the browser's clipboard-permission prompt out of the
 * way of the common case (pasting layers copied in the editor).
 *
 * Always offered rather than greyed out when the in-app clipboard is empty: the
 * OS clipboard may well hold text or an image, and there is no way to know
 * without asking.
 */
export async function pasteHere(store, at) {
    if (store.clipboard?.length) { store.pasteClipboard({ at }); return; }
    try {
        const text = await navigator.clipboard?.readText?.();
        if (text) store.pasteClipboard({ systemText: text, at });
    } catch {
        // Denied, unsupported, or nothing there — ⌘V still works either way.
    }
}

/**
 * Rows for a selection of ELEMENTS.
 *
 * @param store   live store snapshot (usePrintEditorStore.getState())
 * @param mode    'admin' | 'user'
 * @param ids     the selection the menu acts on
 * @param at      canvas point to paste at, or null for the store's own default
 * @param extra   rows spliced in after the clipboard block (the canvas's
 *                "Select layer" picker; empty in the panel)
 * @param onRename  when given, adds Figma's Rename row — the panel starts an
 *                  inline edit, the canvas has nowhere to type so it omits it
 */
export function buildElementMenu({
    store, mode, ids = [], at = null, extra = [], onRename = null,
}) {
    const elements = store.document?.elements || [];
    const chosen = elements.filter((el) => ids.includes(el.id));
    const one = chosen.length === 1 ? chosen[0] : null;
    const admin = mode === 'admin';
    const canGroup = chosen.length >= 2 && admin;
    const hasGroup = chosen.some((el) => el.groupId);
    const maskedHere = chosen.some((el) => el.maskId || el.isMask);
    const hidden = one ? one.visible === false : chosen.every((el) => el.visible === false);
    const adminLocked = one ? !!one.adminLocked : chosen.every((el) => el.adminLocked);
    const userLocked = one ? !!one.locked : chosen.every((el) => el.locked);
    const cropping = !!one && store.croppingId === one.id;

    return [
        { label: 'Copy', shortcut: '$mod C', icon: IconCopy, onSelect: () => store.copySelected() },
        {
            label: 'Cut',
            shortcut: '$mod X',
            icon: IconCut,
            disabled: !admin,
            onSelect: () => store.cutSelected(),
        },
        {
            label: 'Paste here',
            shortcut: '$mod V',
            icon: IconClipboard,
            onSelect: () => pasteHere(store, at),
        },
        {
            label: 'Duplicate',
            shortcut: '$mod D',
            icon: IconCopyPlus,
            onSelect: () => store.duplicateSelected(),
        },
        ...(onRename
            ? [{
                label: 'Rename',
                shortcut: '$mod R',
                icon: IconCursorText,
                // The caller decided WHAT this renames — a layer row renames its
                // layer, a group row its group — so a multi-element selection is
                // no reason to grey it out.
                disabled: !admin,
                onSelect: () => onRename(),
            }]
            : []),
        ...extra,
        { separator: true },
        {
            label: 'Bring to front', shortcut: ']', icon: IconArrowBarToUp, onSelect: () => store.bringToFront(ids),
        },
        {
            label: 'Bring forward', icon: IconArrowUp, onSelect: () => ids.forEach((id) => store.bringForward(id)),
        },
        {
            label: 'Send backward', icon: IconArrowDown, onSelect: () => ids.forEach((id) => store.sendBackward(id)),
        },
        {
            label: 'Send to back', shortcut: '[', icon: IconArrowBarToDown, onSelect: () => store.sendToBack(ids),
        },
        { separator: true },
        {
            label: 'Group selection',
            shortcut: '$mod G',
            icon: IconBoxMultiple,
            disabled: !canGroup,
            onSelect: () => store.groupSelected(),
        },
        {
            label: 'Ungroup',
            shortcut: '$mod ⇧ G',
            icon: IconBoxMultipleFilled,
            disabled: !hasGroup || !admin,
            onSelect: () => store.ungroupSelected(),
        },
        {
            // Figma's rule: the LOWEST selected layer becomes the mask.
            label: 'Use as mask',
            // Ctrl, not the platform's command key — that is what the canvas
            // listens for on every platform (see CanvasViewport's keydown).
            shortcut: '⌃ M',
            icon: IconMask,
            disabled: chosen.length < 2 || !admin,
            onSelect: () => store.maskSelected(ids),
        },
        {
            label: 'Release mask',
            icon: IconMaskOff,
            disabled: !maskedHere || !admin,
            onSelect: () => store.releaseMask(ids),
        },
        { separator: true },
        {
            // A shape drawn from the catalogue becomes an editable outline the
            // moment this is used — see startPathEdit.
            label: store.editingPathId === one?.id ? 'Finish editing path' : 'Edit path',
            icon: IconVectorBezier2,
            disabled: !one || one.type !== ELEMENT_TYPES.SHAPE || !admin,
            onSelect: () => (store.editingPathId === one.id
                ? store.stopPathEdit()
                : store.startPathEdit(one.id)),
        },
        {
            label: cropping ? 'Finish crop' : 'Crop image',
            icon: IconCrop,
            disabled: !one || one.type !== ELEMENT_TYPES.IMAGE || !one.src,
            onSelect: () => (cropping ? store.stopCrop() : store.startCrop(one.id)),
        },
        {
            label: 'Flip horizontal', shortcut: '⇧ H', icon: IconFlipHorizontal, onSelect: () => store.flipSelected('x', ids),
        },
        {
            label: 'Flip vertical', shortcut: '⇧ V', icon: IconFlipVertical, onSelect: () => store.flipSelected('y', ids),
        },
        { separator: true },
        {
            label: hidden ? 'Show' : 'Hide',
            shortcut: '⇧ $mod H',
            icon: hidden ? IconEye : IconEyeOff,
            onSelect: () => store.toggleVisibility(ids),
        },
        {
            label: adminLocked ? 'Unlock here (admin)' : 'Lock here (admin)',
            shortcut: '⇧ $mod L',
            icon: adminLocked ? IconLockOpen : IconLock,
            disabled: !admin,
            onSelect: () => store.toggleLock(ids),
        },
        {
            label: userLocked ? 'Unlock for user' : 'Lock for user',
            // Control on every platform — Cmd+Option+L is the browser's Downloads page.
            shortcut: '⌥ ⌃ L',
            icon: userLocked ? IconLockOpen : IconLock,
            disabled: !admin,
            onSelect: () => store.toggleUserLock(ids),
        },
        { separator: true },
        {
            label: 'Delete', shortcut: '⌫', icon: IconTrash, onSelect: () => store.deleteSelected(),
        },
    ];
}

/**
 * Rows for a FRAME header. A frame is a container, not a layer: it has no
 * z-order among its siblings and nothing to mask or flip, so it gets its own
 * short menu rather than a mostly-disabled copy of the one above.
 */
export function buildFrameMenu({
    store, mode, frame, onRename = null,
}) {
    const admin = mode === 'admin';
    const inFrame = (store.document?.elements || [])
        .filter((el) => el.frameId === frame.id)
        .map((el) => el.id);

    return [
        ...(onRename
            ? [{
                label: 'Rename',
                shortcut: '$mod R',
                icon: IconCursorText,
                disabled: !admin,
                onSelect: () => onRename(),
            }]
            : []),
        {
            label: 'Duplicate frame',
            shortcut: '$mod D',
            icon: IconCopyPlusFilled,
            disabled: !admin,
            onSelect: () => store.duplicateFrame(frame.id),
        },
        {
            label: 'Add frame',
            shortcut: 'F',
            icon: IconFrame,
            disabled: !admin,
            onSelect: () => store.addFrame(),
        },
        { separator: true },
        {
            label: 'Select all in frame',
            shortcut: '$mod A',
            icon: IconSelectAll,
            disabled: !inFrame.length,
            onSelect: () => store.select(inFrame),
        },
        { separator: true },
        {
            label: 'Delete frame',
            shortcut: '⌫',
            icon: IconTrash,
            // The last frame IS the document — `deleteFrame` would happily take it
            // and leave nothing to draw on, so the offer stops here.
            disabled: !admin || getFrames(store.document).length < 2,
            onSelect: () => store.deleteFrame(frame.id),
        },
    ];
}
